import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Code2, Globe, CheckCircle,
  Zap, Award, Target, Eye,
  TrendingUp, ShieldCheck, Star
} from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import './About.css';

const IconMap = {
  Zap: <Zap size={24} />,
  Award: <Award size={24} />,
  Eye: <Eye size={24} />,
  TrendingUp: <TrendingUp size={24} />,
  ShieldCheck: <ShieldCheck size={24} />,
  Star: <Star size={24} />,
  Target: <Target size={24} />,
  Code2: <Code2 size={24} />,
  Globe: <Globe size={24} />,
  CheckCircle: <CheckCircle size={24} />
};

const About = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState([]);
  const [mission, setMission] = useState(null);
  const [values, setValues] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiBase = `${process.env.REACT_APP_API_URL || ''}/api/about`;

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchData = async () => {
      try {
        const [statsRes, missionRes, valuesRes, milestonesRes, teamRes] = await Promise.all([
          fetch(`${apiBase}/stats`).then(res => res.json()),
          fetch(`${apiBase}/mission`).then(res => res.json()),
          fetch(`${apiBase}/values`).then(res => res.json()),
          fetch(`${apiBase}/milestones`).then(res => res.json()),
          fetch(`${apiBase}/team`).then(res => res.json())
        ]);

        setStats(Array.isArray(statsRes) ? statsRes : []);
        setMission(missionRes || null);
        setValues(Array.isArray(valuesRes) ? valuesRes : []);
        setMilestones(Array.isArray(milestonesRes) ? milestonesRes : []);
        setTeam(Array.isArray(teamRes) ? teamRes : []);
      } catch (err) {
        console.error('Error fetching about data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiBase]);

  const getLangKey = (obj, key) => {
    const lang = i18n.language ? i18n.language.split('-')[0] : 'en';
    return obj[`${key}_${lang}`] || obj[`${key}_en`] || '';
  };

  if (loading) return null;

  return (
    <>
      <Navbar />

      <div className="about-page-container">
        <section className="about-content-section">
          <motion.h1
            className="about-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span>Nexova Dev LLC</span> <br /> Digital Architects
          </motion.h1>
          <motion.p
            className="about-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('about.text')}
          </motion.p>

          <div className="about-stats-grid">
            {stats.map((stat, i) => (
              <motion.div
                className="about-stat-card"
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <h4>{stat.value}</h4>
                <span>{getLangKey(stat, 'label')}</span>
              </motion.div>
            ))}
            {stats.length === 0 && (
              [
                { val: '150+', label: t('about.stats.projects') },
                { val: '5+', label: t('about.stats.years') },
                { val: '50+', label: 'Experts' },
                { val: '24/7', label: t('about.stats.support') }
              ].map((stat, i) => (
                <motion.div className="about-stat-card" key={i}>
                  <h4>{stat.val}</h4>
                  <span>{stat.label}</span>
                </motion.div>
              ))
            )}
          </div>

          <motion.div
            className="about-mission-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="icon"><Target size={40} /></div>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '20px' }}>
              {mission ? getLangKey(mission, 'title') : t('about.mission.title')}
            </h2>
            <p style={{ fontSize: '1.25rem', color: 'var(--text-muted)', lineHeight: '1.8' }}>
              {mission ? getLangKey(mission, 'content') : t('about.mission.text')}
            </p>
          </motion.div>

          <motion.h2 className="about-title" style={{ fontSize: '3rem', marginBottom: '60px' }}>{t('about.values.title')}</motion.h2>
          <div className="about-values-grid">
            {(values.length > 0 ? values : []).map((v, i) => (
              <motion.div
                className="about-value-card"
                key={v.id || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon-box">{IconMap[v.icon] || <Zap size={24} />}</div>
                <h3>{getLangKey(v, 'title')}</h3>
                <p>{getLangKey(v, 'desc')}</p>
              </motion.div>
            ))}
            {values.length === 0 && (
              [1, 2, 3, 4, 5, 6].map(i => (
                <motion.div className="about-value-card" key={i}>
                  <div className="icon-box"><Zap size={24} /></div>
                  <h3>{t(`about.values.v${i}.t`)}</h3>
                  <p>{t(`about.values.v${i}.d`)}</p>
                </motion.div>
              ))
            )}
          </div>

          <div className="about-milestone-section">
            <motion.h2 className="about-title" style={{ fontSize: '3rem', marginBottom: '40px' }}>{t('about.story.title')}</motion.h2>
            <div className="about-milestone-container">
              {(milestones.length > 0 ? milestones : []).map((m, i) => (
                <motion.div
                  className="about-milestone-item"
                  key={m.id || i}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="year">{m.year}</div>
                  <h4>{getLangKey(m, 'title')}</h4>
                  <p>{getLangKey(m, 'desc')}</p>
                </motion.div>
              ))}
              {milestones.length === 0 && (
                (t('about.milestones', { returnObjects: true }) || []).map((m, i) => (
                  <motion.div className="about-milestone-item" key={i}>
                    <div className="year">{m.year}</div>
                    <h4>{m.t}</h4>
                    <p>{m.d}</p>
                  </motion.div>
                ))
              )}
            </div>
          </div>

          <motion.h2 className="about-title" style={{ fontSize: '3rem', marginBottom: '40px' }}>{t('about.team.title')}</motion.h2>
          <div className="about-team-grid">
            {(team.length > 0 ? team : []).map((member, i) => (
              <motion.div
                className="about-team-card"
                key={member.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="avatar">
                  {member.image_url ? <img src={member.image_url} alt={member.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : member.emoji}
                </div>
                <h4>{member.name}</h4>
                <span>{getLangKey(member, 'role')}</span>
              </motion.div>
            ))}
          </div>

          <div style={{
            marginTop: '120px',
            padding: '100px 40px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)',
            border: '1px solid var(--glass-border)',
            borderRadius: '40px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <h2 style={{ fontSize: '3rem', marginBottom: '24px' }}>Ready to Scale Your Vision?</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '1.25rem', marginBottom: '48px', maxWidth: '600px', margin: '0 auto 48px' }}>
              Join hundreds of businesses that trust Nexova Dev for their critical technology infrastructure and development.
            </p>
            <Link to="/contact" className="about-action-button">
              Get a Free Consultation
            </Link>
          </div>

        </section>
      </div>
      <Footer />
    </>
  );
};

export default About;

